import {
    type FC
} from "react";
import {
    View
} from "react-native";
import type IAttachmentGridProps from "./type";
import stylesheet, {
    ATTACHMENT_GRID_MAX_TILES,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    formatNumber
} from "../../../../helpers/locale";
import AttachmentGridTile from "../gridTile";

const AttachmentGrid: FC<IAttachmentGridProps> = ({
    customLocalize,
    maxVisible = 4,
    customTheme,
    onPress,
    images,
    style,
    ...props
}) => {
    const {
        shapeConfigs,
        radiuses,
        borders
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const visibleImages = images.slice(0, Math.min(Math.max(maxVisible, 1), ATTACHMENT_GRID_MAX_TILES));

    const hiddenCount = images.length - visibleImages.length;

    const {
        container: containerDynamicStyle,
        gap: gapDynamicStyle
    } = useStyles({
        count: visibleImages.length,
        shapeConfigs,
        radiuses,
        borders
    });

    if(visibleImages.length === 0) {
        return null;
    }

    const renderTile = (index: number) => {
        const image = visibleImages[index];

        if(!image) {
            return null;
        }

        const isMore = hiddenCount > 0 && index === visibleImages.length - 1;

        const getLabel = () => {
            if(images.length === 1) {
                return localize("image-label", [
                    image.alt
                ]);
            }

            const positionLabel = localize("image-position-label", [
                formatNumber({
                    locale: activeLocale,
                    value: index + 1
                }),
                formatNumber({
                    value: images.length,
                    locale: activeLocale
                }),
                image.alt
            ]);

            return isMore ? localize("image-more-label", [
                positionLabel,
                formatNumber({
                    locale: activeLocale,
                    value: hiddenCount
                })
            ]) : positionLabel;
        };

        return <AttachmentGridTile
            moreText={isMore ? `+${formatNumber({
                locale: activeLocale,
                value: hiddenCount
            })}` : undefined}
            customTheme={customTheme}
            source={image.source}
            label={getLabel()}
            onPress={onPress}
            index={index}
            key={index}
        />;
    };

    const renderLayout = () => {
        if(visibleImages.length === 3) {
            return <View
                style={[
                    stylesheet.row,
                    gapDynamicStyle
                ]}
            >
                {renderTile(0)}
                <View
                    style={[
                        stylesheet.column,
                        gapDynamicStyle
                    ]}
                >
                    {renderTile(1)}
                    {renderTile(2)}
                </View>
            </View>;
        }

        if(visibleImages.length >= 4) {
            return <>
                <View
                    style={[
                        stylesheet.row,
                        gapDynamicStyle
                    ]}
                >
                    {renderTile(0)}
                    {renderTile(1)}
                </View>
                <View
                    style={[
                        stylesheet.row,
                        gapDynamicStyle
                    ]}
                >
                    {renderTile(2)}
                    {renderTile(3)}
                </View>
            </>;
        }

        return <View
            style={[
                stylesheet.row,
                gapDynamicStyle
            ]}
        >
            {visibleImages.map((_, index) => renderTile(index))}
        </View>;
    };

    return <View
        {...props}
        aria-label={localize("images-count", [
            formatNumber({
                value: images.length,
                locale: activeLocale
            })
        ])}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="group"
    >
        {renderLayout()}
    </View>;
};
export default AttachmentGrid;
